import {
  Button,
  ButtonGroup,
  Chip,
  Form,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  useDisclosure,
} from '@heroui/react'
import { useForm } from '@tanstack/react-form'
import { produce } from 'immer'
import { Plus, Trash } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import ConfirmDeleteModal from './ConfirmDeleteModal'
import EditRowButton from './EditRowButton'
import { tableClassNames, usePatchSchema, useSchema } from './utils'

function GroupingModal({ grouping }) {
  const { isOpen, onOpen, onOpenChange } = useDisclosure()
  const [searchParams] = useSearchParams()
  const id = searchParams.get('schema')

  const { data: selectedSchema } = useSchema(id)
  const { mutate: patchSchema } = usePatchSchema()

  const allVars = selectedSchema?.schema?.variables
  const isEdit = !!grouping

  const form = useForm({
    defaultValues: grouping,
    onSubmit: async ({ value }) => {
      const variablesArray = Array.from(value?.variables || [])
      const validVars = variablesArray.filter(variable => allVars.some(v => v.name === variable))

      const newGrouping = {
        ...value,
        variables: validVars,
      }

      const updatedSchema = produce(selectedSchema, (draft) => {
        draft.schema.groupings = isEdit
          ? draft.schema.groupings.map(g =>
              g.name === grouping.name ? newGrouping : g,
            )
          : [...(draft.schema.groupings ?? []), newGrouping]
      })

      await patchSchema(updatedSchema)
      if (patchSchema.error) {
        toast.error('Failed to update grouping')
      }
      else {
        toast.success('Grouping updated successfully')
      }

      onOpenChange()
    },
  })

  useEffect(() => {
    if (!isOpen) {
      form.reset()
    }
  }, [isOpen])

  return (
    <>
      {isEdit
        ? (
            <EditRowButton onPress={onOpen} />
          )
        : (
            <Button onPress={onOpen} variant="solid" color="primary" size="sm" radius="sm">
              <Plus size={16} />
              Add grouping
            </Button>
          )}
      <Modal
        radius="sm"
        isOpen={isOpen}
        size="xl"
        onOpenChange={onOpenChange}
        classNames={{
          footer: 'ml-auto',
          body: 'flex flex-col gap-5 w-full pt-4',
          header: 'border-b border-gray-200 bg-[#18181b] w-full',
        }}
      >
        <ModalContent>
          {onClose => (
            <Form onSubmit={(e) => {
              e.preventDefault()
              e.stopPropagation()
              form.handleSubmit()
            }}
            >
              <ModalHeader className="flex flex-col gap-1">
                <h2 className={`
                  flex items-center gap-3 font-taurus text-2xl font-semibold
                  text-white
                `}
                >
                  {grouping ? 'Edit grouping' : 'Create new grouping'}
                </h2>
                <p className={`
                  font-taurus text-xs font-light tracking-wider
                  text-foreground-400
                `}
                >
                  Create a new grouping to group extractions together for increased citation accuracy.
                </p>
              </ModalHeader>
              <ModalBody>

                <form.Field
                  name="name"
                  children={(field) => {
                    return (
                      <Input
                        isRequired
                        variant="bordered"
                        radius="sm"
                        labelPlacement="outside"
                        placeholder="Enter a name for your new grouping"
                        description="Give your grouping a name to identify it in the analysis."
                        label="Name"
                        onValueChange={field.handleChange}
                        value={field.state.value}
                      />
                    )
                  }}
                />

                <form.Field
                  name="description"
                  children={(field) => {
                    return (
                      <Input
                        variant="bordered"
                        radius="sm"
                        labelPlacement="outside"
                        placeholder="Enter a description for your new grouping"
                        description="Describe your grouping to help the LLM understand it."
                        label="Description"
                        onValueChange={field.handleChange}
                        value={field.state.value}
                      />
                    )
                  }}
                />

                <form.Field
                  name="variables"
                  children={(field) => {
                    return (
                      <Select
                        variant="bordered"
                        radius="sm"
                        placeholder="Select a grouping"
                        items={allVars}
                        selectionMode="multiple"
                        label="Grouped Variables"
                        description="Choose variables to group together."
                        labelPlacement="outside"
                        selectedKeys={field.state.value}
                        onSelectionChange={field.handleChange}
                        renderValue={(items) => {
                          return (
                            <div className="flex flex-wrap gap-1">
                              {items.map(item => (
                                <Chip size="sm" color="primary" key={item.key}>{item.data.name}</Chip>
                              ))}
                            </div>
                          )
                        }}
                      >
                        {variable => <SelectItem key={variable.name}>{variable.name}</SelectItem>}
                      </Select>
                    )
                  }}
                />
              </ModalBody>
              <ModalFooter>
                <Button color="danger" radius="sm" variant="light" onPress={onClose}>
                  Close
                </Button>
                <Button color="primary" radius="sm" type="submit">
                  {isEdit ? 'Update' : 'Create'}
                </Button>
              </ModalFooter>
            </Form>
          )}
        </ModalContent>
      </Modal>
    </>
  )
}

export default function GroupingsTable() {
  const [isDeleting, setIsDeleting] = useState(false)
  const [groupingToDelete, setGroupingToDelete] = useState(null)

  const [searchParams] = useSearchParams()
  const id = searchParams.get('schema')

  const { data: selectedSchema } = useSchema(id)
  const { mutate: patchSchema } = usePatchSchema()

  const groupings = useMemo(() => selectedSchema?.schema?.groupings || [], [selectedSchema])

  async function handleDeleteGrouping(groupingName) {
    const updatedSchema = produce(selectedSchema, (draft) => {
      draft.schema.groupings = draft.schema.groupings.filter(
        grouping => grouping.name !== groupingName,
      )
    })

    await patchSchema(updatedSchema)

    if (patchSchema.error) {
      toast.error('Failed to delete grouping')
    }
    else {
      toast.success('Grouping deleted successfully')
    }
    setIsDeleting(false)
  }

  return (
    <div className="relative w-full">
      <Table
        radius="none"
        classNames={tableClassNames}
        aria-label="Groupings"
      >
        <TableHeader>
          <TableColumn>Name</TableColumn>
          <TableColumn>Description</TableColumn>
          <TableColumn>Variables</TableColumn>
          <TableColumn align="end" />
        </TableHeader>
        <TableBody emptyContent={(
          <div className="mt-24 flex h-full w-full flex-col items-center">
            <h1 className="mb-6 text-3xl font-semibold tracking-tighter">
              No groupings yet.
            </h1>
          </div>
        )}
        >
          {groupings?.map(grouping => (
            <TableRow key={grouping.name}>
              <TableCell className="font-medium">{grouping.name}</TableCell>
              <TableCell className="text-gray-500">{grouping.description}</TableCell>
              <TableCell className="text-gray-500">
                <span className="flex flex-wrap gap-2">
                  {grouping.variables?.map(key => (
                    <Chip key={key} variant="flat" color="primary" size="sm">{key}</Chip>
                  ))}
                </span>
              </TableCell>
              <TableCell>
                <ButtonGroup className="gap-2">
                  <GroupingModal grouping={grouping} />
                  <ConfirmDeleteModal
                    isOpen={isDeleting && groupingToDelete === grouping.name}
                    onOpenChange={() => setIsDeleting(false)}
                    onConfirm={() => handleDeleteGrouping(grouping.name)}
                    title="Delete Grouping"
                    message="Are you sure you want to delete this grouping?"
                  >
                    <Button
                      isIconOnly
                      onPress={() => {
                        setGroupingToDelete(grouping.name)
                        setIsDeleting(true)
                      }}
                      variant="faded"
                      color="danger"
                      size="sm"
                      disableRipple
                    >
                      <Trash size={16} />
                    </Button>
                  </ConfirmDeleteModal>
                </ButtonGroup>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <div className="absolute -right-3.5 -bottom-12">
        <GroupingModal />
      </div>
    </div>
  )
}
